/**
 * Shared presentational helpers for service requests, used by both the
 * compact dashboard widget (ServiceRequestsPanel) and the standalone
 * manager page card (ServiceRequestCard).
 */

const REQUEST_ICONS = {
  call_waiter: '👋',
  request_bill: '💳',
  need_help: '❓',
  water: '💧',
  napkins: '🧻',
  utensils: '🍴'
};

export const getRequestIcon = (type) => REQUEST_ICONS[type] || '🔔';

export const getRequestLabel = (t, type) => {
  const key = `service.${type}`;
  const fallback = type ? type.replace(/_/g, ' ') : '';
  return t(key, { defaultValue: fallback });
};

export const formatTimeAgo = (t, dateStr) => {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 1) return t('service.panel.justNow', { defaultValue: 'Just now' });
  if (mins === 1) return t('service.panel.oneMinAgo', { defaultValue: '1m ago' });
  if (mins < 60) return `${mins}m ago`;
  if (hours === 1) return t('service.panel.oneHourAgo', { defaultValue: '1h ago' });
  if (hours < 24) return `${hours}h ago`;
  if (days === 1) return t('service.panel.oneDayAgo', { defaultValue: '1d ago' });
  return `${days}d ago`;
};

/** Whole minutes a request has been waiting since it was created. */
export const getAgeMinutes = (dateStr) => {
  if (!dateStr) return 0;
  return Math.max(0, Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000));
};

const HIGH_PRIORITIES = new Set(['high', 'urgent']);

/**
 * Escalating urgency tone combining how long a table has waited with its
 * declared priority. Returns 'calm' | 'warning' | 'danger'.
 *   <5m   → calm   (or warning/danger if priority is already high/urgent)
 *   5–10m → warning
 *   >10m  → danger
 */
export const getWaitTone = (minutes, priority) => {
  const high = HIGH_PRIORITIES.has(priority);
  if (minutes > 10 || (high && minutes > 5)) return 'danger';
  if (minutes >= 5 || high) return 'warning';
  return 'calm';
};
